/**
 * FacilityDetailView - Clean, prosecutor-friendly facility detail view
 *
 * Structure:
 * 1. Understaffing Summary (PBJ + MDS with comparisons)
 * 2. Prior Conduct (visual timeline)
 * 3. Wage Analysis
 * 4. Related Party Payments
 */

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Divider,
  Chip,
  Tooltip,
  Button,
  CircularProgress,
  LinearProgress,
  IconButton,
  Menu,
  MenuItem,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import api from '../../api/axios';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import UnderstaffingSummary from './UnderstaffingSummary';
import type { AcuityModel } from './UnderstaffingSummary';
import PriorConduct from './PriorConduct';
import StaffingTrendCard from './StaffingTrendCard';

// ─────────────────────────────────────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────────────────────────────────────

interface FacilityData {
  ccn: string;
  facility_name: string;
  state: string;
  beds?: number;
  avg_census?: number;

  // Actual HPRD (hours per resident day) - RN+LPN+CNA only
  avg_pbj_nursing_hrd?: number | null;
  // Legacy: includes all staff types (RN, LPN, CNA, RNDON, etc.)
  avg_cms_total_staffing_hrd?: number | null;

  // Understaffing metrics
  total_understaffed_state_min_hprd_hrs: number | null;
  total_understaffed_state_min_hprd_cost: number | null;
  pct_days_below_cms_hprd?: number | null;
  understaffed_state_min_hprd_hrs_per_res_day?: number | null;
  total_understaffed_mds_hrs?: number | null;
  total_mds_understaffing_cost?: number | null;

  // Citation metrics (for Prior Conduct)
  total_harm_citations?: number | null;
  total_jeopardy_citations?: number | null;
  harm_per_100_residents?: number | null;
  total_citations_per_100?: number | null;
  total_citations?: number | null;
  total_fine_amount?: number | null;
  staffing_ftags_total?: number | null;
  staffing_harm_total?: number | null;
  // F-tag category totals (2020-2024)
  total_staffing_ftags?: number | null;
  total_abuse_neglect_ftags?: number | null;
  total_quality_of_care_ftags?: number | null;
  total_infection_control_ftags?: number | null;
  total_pharmacy_ftags?: number | null;
  total_behavioral_health_ftags?: number | null;
  total_dietary_ftags?: number | null;

  // Wage analysis
  rn_lpn_wage_diff?: number | null;
  avg_cr_rn_hourly_wage?: number | null;
  avg_cr_lpn_hourly_wage?: number | null;
  avg_cr_cna_hourly_wage?: number | null;
  narrow_rn_lpn_wage_diff?: boolean;

  // Related party (allowable - what CMS considers fair market value)
  total_rp_total?: number | null;
  rp_total_per_resident_day?: number | null;
  rp_total_pct_gross_rev?: number | null;
  rp_mgmt_pct_gross_rev?: number | null;
  total_rp_real_estate?: number | null;
  admin_mgmt_pct_gross_rev?: number | null;
  // Related party (paid - actual payments to related parties)
  total_rp_total_paid?: number | null;
  total_rp_total_adj?: number | null;  // Adjustment (negative = excess over allowable)
  rp_total_paid_per_resident_day?: number | null;
  rp_total_paid_pct_gross_rev?: number | null;
  total_rp_real_estate_paid?: number | null;
  total_rp_real_estate_adj?: number | null;  // Real estate adjustment

  // RP Admin/Management (Cost Center 4)
  total_rp_mgmt_admin?: number | null;  // Allowable
  total_rp_mgmt_admin_paid?: number | null;  // Paid/Included
  total_rp_mgmt_admin_adj?: number | null;  // Adjustment (negative = excess)

  // RP Interest (profit extraction signal - from A-8-1 interest items)
  // Total across all cost centers
  total_rp_interest_paid?: number | null;
  total_rp_interest_allowable?: number | null;
  total_rp_interest_adj?: number | null;
  rp_interest_pct_total_rp?: number | null;
  years_with_rp_interest?: number | null;
  // Interest split by cost center
  total_rp_interest_re_paid?: number | null;       // CC1 Buildings
  total_rp_interest_re_allowable?: number | null;
  total_rp_interest_re_adj?: number | null;
  total_rp_interest_admin_paid?: number | null;    // CC4 Admin
  total_rp_interest_admin_allowable?: number | null;
  total_rp_interest_admin_adj?: number | null;

  // B-I allocations (total costs allocated to SNF)
  total_cap_buildings?: number | null;  // Buildings (Cost Center 1)
  total_cap_equipment?: number | null;  // Equipment (Cost Center 2)
  total_admin_general?: number | null;  // Admin & General (Cost Center 4)

  // Nursing turnover (average across all years - only available from 2022)
  avg_nursing_turnover?: number | null;

  // Wage source: how many years had facility-specific cost report wages
  years_with_cr_wages?: number | null;
  num_years?: number | null;
}

// Turnover benchmark data by year
interface TurnoverYearData {
  median: number | null;
  n: number;
}

interface TurnoverBenchmarks {
  national: Record<string, TurnoverYearData>;
  by_state: Record<string, Record<string, TurnoverYearData>>;
}

// Citation benchmark data by size bucket
interface SizeBucketBenchmark {
  bed_range: string;
  num_facilities: number;
  avg: number;
  median: number;
  p25: number;
  p75: number;
  p90: number;
  avg_harm: number;
  median_harm: number;
  avg_jeopardy: number;
  avg_staffing_ftags: number;
}

interface RPBenchmark {
  median_rp_pct: number | null;
  median_rp_per_resident: number | null;
  median_rp_per_resident_day: number | null;
  median_rp_re_per_resident: number | null;
  median_rp_mgmt_per_resident: number | null;
  median_margin_gap: number | null;
  facilities_with_rp: number;
  // Per-bed benchmarks
  median_rp_re_per_bed?: number | null;
  median_rp_admin_per_bed?: number | null;
  n_with_rp_re?: number | null;
  n_with_rp_admin?: number | null;
}

interface RPBenchmarks {
  national: RPBenchmark | null;
  by_state: Record<string, RPBenchmark>;
  year?: number;
}

interface RPEntity {
  entity_name: string;
  common_owner: string | null;
  relationship_symbol: string | null;
  service_types: string[];
  address?: string;
  city_state_zip?: string;
  tags?: string[];
  in_latest_report?: boolean;
  years?: number[];
}

interface FacilityDetailViewProps {
  facility: FacilityData;
  schemeId: string;
  facilityYears?: any[];
  sweetwaterBaseline?: {
    understaffed_state_min_hprd_hrs_per_res_day: number | null;
    pct_days_below_cms_hprd: number | null;
    citations_per_bed: number | null;
    harm_per_100_residents: number | null;
  };
  citationBenchmarks?: Record<string, Record<string, SizeBucketBenchmark>> | null;
  rpBenchmarks?: RPBenchmarks | null;
  rpEntities?: RPEntity[];
  turnoverBenchmarks?: TurnoverBenchmarks | null;
  // Cost report download
  onGenerateCostReports?: () => void;
  costReportGenerating?: boolean;
  costReportProgress?: number;
  costReportStatus?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────

const formatMoney = (val: number | null | undefined): string => {
  if (val == null) return '-';
  const prefix = val < 0 ? '-$' : '$';
  return `${prefix}${Math.abs(val).toLocaleString()}`;
};

const formatPercent = (val: number | null | undefined): string => {
  if (val == null) return '-';
  return `${val.toFixed(1)}%`;
};

// ─────────────────────────────────────────────────────────────────────────────
// WAGE ANALYSIS SECTION
// ─────────────────────────────────────────────────────────────────────────────

const WageAnalysisSection: React.FC<{ facility: FacilityData }> = ({ facility }) => {
  const hasNarrowGap = facility.narrow_rn_lpn_wage_diff || (facility.rn_lpn_wage_diff != null && facility.rn_lpn_wage_diff < 10);

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" fontWeight={700} mb={2}>
        Wage Analysis
      </Typography>

      <Box display="flex" gap={4} alignItems="flex-start">
        <Tooltip title="Difference between RN and LPN hourly wages. A narrow gap (<$10/hr) may indicate wage suppression or misclassification.">
          <Box sx={{ cursor: 'help' }}>
            <Typography variant="caption" color="text.secondary">RN-LPN Wage Gap</Typography>
            <Typography variant="h5" fontWeight={700} color={hasNarrowGap ? 'warning.main' : 'text.primary'}>
              {facility.rn_lpn_wage_diff != null ? `$${facility.rn_lpn_wage_diff.toFixed(2)}/hr` : '-'}
            </Typography>
            {hasNarrowGap && (
              <Chip size="small" label="Narrow gap" color="warning" sx={{ mt: 0.5 }} />
            )}
          </Box>
        </Tooltip>

        <Divider orientation="vertical" flexItem />

        <Box display="flex" gap={3}>
          <Tooltip title="Average RN hourly wage from cost reports">
            <Box sx={{ cursor: 'help' }}>
              <Typography variant="caption" color="text.secondary">Avg RN Wage</Typography>
              <Typography variant="body1" fontWeight={500}>
                {facility.avg_cr_rn_hourly_wage != null ? `$${facility.avg_cr_rn_hourly_wage.toFixed(2)}/hr` : '-'}
              </Typography>
            </Box>
          </Tooltip>

          <Tooltip title="Average LPN hourly wage from cost reports">
            <Box sx={{ cursor: 'help' }}>
              <Typography variant="caption" color="text.secondary">Avg LPN Wage</Typography>
              <Typography variant="body1" fontWeight={500}>
                {facility.avg_cr_lpn_hourly_wage != null ? `$${facility.avg_cr_lpn_hourly_wage.toFixed(2)}/hr` : '-'}
              </Typography>
            </Box>
          </Tooltip>

          <Tooltip title="Average CNA hourly wage from cost reports">
            <Box sx={{ cursor: 'help' }}>
              <Typography variant="caption" color="text.secondary">Avg CNA Wage</Typography>
              <Typography variant="body1" fontWeight={500}>
                {facility.avg_cr_cna_hourly_wage != null ? `$${facility.avg_cr_cna_hourly_wage.toFixed(2)}/hr` : '-'}
              </Typography>
            </Box>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// NURSING TURNOVER SECTION
// ─────────────────────────────────────────────────────────────────────────────

interface NursingTurnoverSectionProps {
  facility: FacilityData;
  facilityYears?: any[];
  turnoverBenchmarks?: TurnoverBenchmarks | null;
}

const NursingTurnoverSection: React.FC<NursingTurnoverSectionProps> = ({
  facility,
  facilityYears,
  turnoverBenchmarks,
}) => {
  // Extract turnover data from facility years (only available 2022+)
  // Note: nursing_turnover comes as string from Django DecimalField, so convert to number
  // Filter out 0 values since they indicate missing data (0% turnover is unrealistic)
  const turnoverByYear = facilityYears
    ?.filter((fy: any) => fy.year >= 2022 && fy.nursing_turnover != null && parseFloat(fy.nursing_turnover) > 0)
    .sort((a: any, b: any) => a.year - b.year)
    .map((fy: any) => ({
      year: fy.year,
      turnover: parseFloat(fy.nursing_turnover),
    }))
    .filter((y) => !isNaN(y.turnover) && y.turnover > 0) || [];

  // Get state and national benchmarks for the facility's state
  const stateBenchmarks = turnoverBenchmarks?.by_state?.[facility.state] || {};
  const nationalBenchmarks = turnoverBenchmarks?.national || {};

  // Calculate averages for comparison
  const facilityAvgTurnover = turnoverByYear.length > 0
    ? turnoverByYear.reduce((sum, y) => sum + y.turnover, 0) / turnoverByYear.length
    : null;

  // Get latest year's data for comparison (most recent with data)
  const latestYear = turnoverByYear.length > 0 ? turnoverByYear[turnoverByYear.length - 1] : null;
  const latestStateMedian = latestYear ? stateBenchmarks[latestYear.year]?.median : null;
  const latestNationalMedian = latestYear ? nationalBenchmarks[latestYear.year]?.median : null;

  // If no turnover data, show simple message
  if (turnoverByYear.length === 0) {
    return (
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" fontWeight={700} mb={2}>
          Nursing Turnover
        </Typography>
        <Box
          sx={{
            p: 2.5,
            backgroundColor: 'grey.50',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography color="text.secondary">
            This facility does not have nursing turnover data.
          </Typography>
        </Box>
      </Box>
    );
  }

  // Calculate how facility compares
  const stateComparison = latestYear && latestStateMedian != null
    ? ((latestYear.turnover - latestStateMedian) / latestStateMedian * 100)
    : null;
  const nationalComparison = latestYear && latestNationalMedian != null
    ? ((latestYear.turnover - latestNationalMedian) / latestNationalMedian * 100)
    : null;

  // Determine severity color based on turnover rate
  const getColorForTurnover = (turnover: number): string => {
    if (turnover >= 60) return 'error.main';
    if (turnover >= 50) return 'warning.main';
    return 'success.main';
  };

  const latestTurnoverColor = latestYear ? getColorForTurnover(latestYear.turnover) : 'text.primary';

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" fontWeight={700} mb={2}>
        Nursing Turnover
      </Typography>

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 3,
          py: 2,
          px: 2,
          backgroundColor: 'grey.50',
          borderRadius: 2,
        }}
      >
        {/* Current Turnover Card */}
        <Box
          sx={{
            flex: 1,
            minWidth: 280,
            maxWidth: 360,
            p: 2.5,
            backgroundColor: 'white',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="overline" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 1 }}>
            Latest Turnover ({latestYear?.year})
          </Typography>

          <Box display="flex" alignItems="baseline" gap={1} mb={2}>
            <Typography variant="h3" fontWeight={700} sx={{ color: latestTurnoverColor }}>
              {latestYear?.turnover.toFixed(1)}%
            </Typography>
            <Typography variant="body2" color="text.secondary">
              annual nursing turnover
            </Typography>
          </Box>

          {/* Comparisons */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {latestStateMedian != null && stateComparison != null && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  vs {facility.state} Median ({latestStateMedian.toFixed(1)}%)
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight={700}
                  sx={{ color: stateComparison > 0 ? 'error.main' : 'success.main' }}
                >
                  {stateComparison > 0 ? '+' : ''}{stateComparison.toFixed(0)}%
                </Typography>
              </Box>
            )}
            {latestNationalMedian != null && nationalComparison != null && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  vs National Median ({latestNationalMedian.toFixed(1)}%)
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight={700}
                  sx={{ color: nationalComparison > 0 ? 'error.main' : 'success.main' }}
                >
                  {nationalComparison > 0 ? '+' : ''}{nationalComparison.toFixed(0)}%
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        {/* Year-over-Year Trend Card */}
        <Box
          sx={{
            flex: 1,
            minWidth: 340,
            maxWidth: 500,
            p: 2.5,
            backgroundColor: 'white',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="overline" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 1.5 }}>
            Turnover Trend (2022-2025)
          </Typography>

          {/* Bar chart showing year-over-year with benchmarks */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {turnoverByYear.map((yearData) => {
              const stateMedian = stateBenchmarks[yearData.year]?.median;
              const nationalMedian = nationalBenchmarks[yearData.year]?.median;
              const maxValue = Math.max(yearData.turnover, stateMedian || 0, nationalMedian || 0, 70);
              const facilityWidth = (yearData.turnover / maxValue) * 100;
              const stateWidth = stateMedian ? (stateMedian / maxValue) * 100 : 0;
              const nationalWidth = nationalMedian ? (nationalMedian / maxValue) * 100 : 0;

              return (
                <Box key={yearData.year}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                    <Typography variant="caption" fontWeight={600}>{yearData.year}</Typography>
                    <Box display="flex" gap={2}>
                      <Typography variant="caption" fontWeight={700} sx={{ color: getColorForTurnover(yearData.turnover) }}>
                        {yearData.turnover.toFixed(1)}%
                      </Typography>
                      {stateMedian != null && (
                        <Typography variant="caption" color="text.secondary">
                          {facility.state}: {stateMedian.toFixed(1)}%
                        </Typography>
                      )}
                      {nationalMedian != null && (
                        <Typography variant="caption" color="text.secondary">
                          US: {nationalMedian.toFixed(1)}%
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  {/* Stacked bar visualization */}
                  <Box sx={{ position: 'relative', height: 20, backgroundColor: 'grey.200', borderRadius: 1, overflow: 'hidden' }}>
                    {/* National median marker (dashed line) */}
                    {nationalMedian != null && (
                      <Box
                        sx={{
                          position: 'absolute',
                          left: `${nationalWidth}%`,
                          top: 0,
                          bottom: 0,
                          width: 2,
                          backgroundColor: 'info.light',
                          zIndex: 1,
                        }}
                      />
                    )}
                    {/* State median marker */}
                    {stateMedian != null && (
                      <Box
                        sx={{
                          position: 'absolute',
                          left: `${stateWidth}%`,
                          top: 0,
                          bottom: 0,
                          width: 2,
                          backgroundColor: 'grey.500',
                          zIndex: 2,
                        }}
                      />
                    )}
                    {/* Facility bar */}
                    <Box
                      sx={{
                        height: '100%',
                        width: `${facilityWidth}%`,
                        backgroundColor: getColorForTurnover(yearData.turnover),
                        borderRadius: 1,
                        transition: 'width 0.5s ease-out',
                      }}
                    />
                  </Box>
                </Box>
              );
            })}
          </Box>

          {/* Legend */}
          <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 12, height: 12, backgroundColor: 'warning.main', borderRadius: 0.5 }} />
              <Typography variant="caption" color="text.secondary">Facility</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 12, height: 2, backgroundColor: 'grey.500' }} />
              <Typography variant="caption" color="text.secondary">{facility.state} Median</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 12, height: 2, backgroundColor: 'info.light' }} />
              <Typography variant="caption" color="text.secondary">National Median</Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        Nursing turnover is the percentage of nursing staff who left the facility within a 12-month period.
        High turnover disrupts care continuity and is associated with poorer resident outcomes.
      </Typography>

      <Divider sx={{ mt: 3 }} />
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// RP ENTITIES EXPANDABLE SECTION
// ─────────────────────────────────────────────────────────────────────────────

const RELATIONSHIP_SYMBOLS: Record<string, string> = {
  'A': 'Individual has financial interest (stockholder, partner, etc.) in both related organization and in provider.',
  'B': 'Corporation, partnership or other organization has financial interest in provider.',
  'C': 'Provider has financial interest in corporation, partnership, or other organization.',
  'D': 'Director, officer, administrator or key person of provider or relative of such person has financial interest in related organization.',
  'E': 'Individual is director, officer, administrator or key person of provider and related organization.',
  'F': 'Director, officer, administrator or key person of related organization or relative of such person has financial interest in provider.',
  'G': 'Other (financial or non-financial).',
};

const RPEntitiesSection: React.FC<{ entities: RPEntity[] }> = ({ entities }) => {
  return (
    <Box
      sx={{
        mt: 2,
        mb: 2,
        p: 2,
        backgroundColor: 'white',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Typography variant="overline" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 1 }}>
        Related Party Entities ({entities.length})
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        Organizations receiving related party payments per CMS cost report (Worksheet A-8-1)
      </Typography>

      <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
        <Box component="thead">
          <Box component="tr" sx={{ borderBottom: '2px solid', borderColor: 'divider' }}>
            <Box component="th" sx={{ textAlign: 'left', py: 1, px: 1, fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', color: 'text.secondary', letterSpacing: 0.5 }}>Name</Box>
            <Box component="th" sx={{ textAlign: 'left', py: 1, px: 1, fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', color: 'text.secondary', letterSpacing: 0.5 }}>Years</Box>
            <Box component="th" sx={{ textAlign: 'left', py: 1, px: 1, fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', color: 'text.secondary', letterSpacing: 0.5 }}>Type of Business</Box>
            <Box component="th" sx={{ textAlign: 'left', py: 1, px: 1, fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', color: 'text.secondary', letterSpacing: 0.5 }}>Common Owner/Controller</Box>
            <Box component="th" sx={{ textAlign: 'left', py: 1, px: 1, fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', color: 'text.secondary', letterSpacing: 0.5 }}>Relationship</Box>
          </Box>
        </Box>
        <Box component="tbody">
          {entities.map((entity, idx) => {
            const yearRange = entity.years && entity.years.length > 0
              ? entity.years.length === 1
                ? `${entity.years[0]}`
                : `${entity.years[0]}–${entity.years[entity.years.length - 1]}`
              : '-';
            const symbol = entity.relationship_symbol?.toUpperCase();
            const symbolDesc = symbol ? RELATIONSHIP_SYMBOLS[symbol] : null;

            return (
              <Box
                component="tr"
                key={idx}
                sx={{
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  '&:hover': { backgroundColor: 'grey.50' },
                  opacity: entity.in_latest_report === false ? 0.6 : 1,
                }}
              >
                <Box component="td" sx={{ py: 1, px: 1, fontWeight: 600 }}>
                  {entity.entity_name}
                </Box>
                <Box component="td" sx={{ py: 1, px: 1, whiteSpace: 'nowrap', color: entity.in_latest_report === false ? 'text.disabled' : 'text.secondary', fontStyle: entity.in_latest_report === false ? 'italic' : 'normal' }}>
                  {yearRange}
                </Box>
                <Box component="td" sx={{ py: 1, px: 1 }}>
                  {entity.service_types.length > 0
                    ? entity.service_types.map(st => st.toLowerCase()).join(', ')
                    : '-'
                  }
                </Box>
                <Box component="td" sx={{ py: 1, px: 1 }}>
                  {entity.common_owner || '-'}
                </Box>
                <Box component="td" sx={{ py: 1, px: 1 }}>
                  {symbol ? (
                    <Box sx={{ display: 'inline-flex', alignItems: 'flex-start', gap: 0.5 }}>
                      <Chip label={symbol} size="small" sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700, minWidth: 24, flexShrink: 0 }} />
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', lineHeight: 1.4 }}>
                        {symbolDesc}
                      </Typography>
                    </Box>
                  ) : '-'}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// RELATED PARTY SECTION
// ─────────────────────────────────────────────────────────────────────────────
// RP YEAR TABLE WITH EXPANDABLE COST CENTERS
// ─────────────────────────────────────────────────────────────────────────────

const rpFmtMoney = (v: number | null | undefined) => {
  if (v == null || v === 0) return '-';
  const absVal = Math.abs(v);
  if (absVal >= 1000000) return `$${(absVal / 1000000).toFixed(1)}M`;
  if (absVal >= 1000) return `$${Math.round(absVal / 1000).toLocaleString()}K`;
  return `$${Math.round(absVal).toLocaleString()}`;
};

interface CostCenterRow {
  costCenter: string;
  label: string;
  paid: number | null;
  allowable: number | null;
  excess: number | null;
  pctRev: number | null;
}

const buildCostCenters = (fy: any): CostCenterRow[] => {
  const m = fy.metadata || fy;
  const rows: CostCenterRow[] = [];

  const add = (costCenter: string, label: string, paidField: string, allowableField: string, adjField: string | null) => {
    const paid = m[paidField] ?? m[allowableField];
    const allowable = m[allowableField];
    const adj = adjField ? m[adjField] : null;
    if (paid && paid > 0) {
      rows.push({ costCenter, label, paid, allowable, excess: adj, pctRev: null });
    }
  };

  add('', 'Capital — Buildings & Fixtures', 'rp_real_estate_paid', 'rp_real_estate', 'rp_real_estate_adj');
  add('', 'Capital — Moveable Equipment', 'rp_equipment_paid', 'rp_equipment', 'rp_equipment_adj');
  add('', 'Employee Benefits', 'rp_benefits_paid', 'rp_benefits', 'rp_benefits_adj');
  add('', 'Administrative & General', 'rp_mgmt_admin_paid', 'rp_mgmt_admin', 'rp_mgmt_admin_adj');
  add('', 'Plant Operation & Maintenance', 'rp_plant_ops_paid', 'rp_plant_ops', 'rp_plant_ops_adj');
  add('', 'Laundry & Linen Service', 'rp_laundry_paid', 'rp_laundry', 'rp_laundry_adj');
  add('', 'Housekeeping', 'rp_housekeeping_paid', 'rp_housekeeping', 'rp_housekeeping_adj');
  add('', 'Dietary', 'rp_dietary_paid', 'rp_dietary', 'rp_dietary_adj');
  add('', 'Nursing Administration', 'rp_nursing_admin_paid', 'rp_nursing_admin', 'rp_nursing_admin_adj');
  add('', 'Central Services & Supply', 'rp_central_svcs_paid', 'rp_central_svcs', 'rp_central_svcs_adj');
  add('', 'Pharmacy', 'rp_pharmacy_paid', 'rp_pharmacy', 'rp_pharmacy_adj');
  add('', 'Medical Records & Library', 'rp_medical_records_paid', 'rp_medical_records', 'rp_medical_records_adj');
  add('', 'Social Service', 'rp_social_service_paid', 'rp_social_service', 'rp_social_service_adj');
  add('', 'Nursing & Allied Health Ed.', 'rp_education_paid', 'rp_education', 'rp_education_adj');
  add('', 'Skilled Nursing Facility', 'rp_direct_nursing_paid', 'rp_direct_nursing', 'rp_direct_nursing_adj');
  add('', 'Nursing Facility', 'rp_nursing_facility_paid', 'rp_nursing_facility', 'rp_nursing_facility_adj');
  add('', 'Physical Therapy', 'rp_physical_therapy_paid', 'rp_physical_therapy', 'rp_physical_therapy_adj');
  add('', 'Occupational Therapy', 'rp_occupational_therapy_paid', 'rp_occupational_therapy', 'rp_occupational_therapy_adj');
  add('', 'Speech Pathology', 'rp_speech_pathology_paid', 'rp_speech_pathology', 'rp_speech_pathology_adj');
  add('', 'Other Cost Centers', 'rp_other_paid', 'rp_other', 'rp_other_adj');

  // Sort by paid descending
  rows.sort((a, b) => (b.paid ?? 0) - (a.paid ?? 0));

  // Add "Unallocated" if known cost centers don't sum to total
  const totalPaid = m.rp_total_paid ?? m.rp_total ?? 0;
  const allocatedPaid = rows.reduce((s, r) => s + (r.paid ?? 0), 0);
  const unallocatedPaid = totalPaid - allocatedPaid;
  if (unallocatedPaid > 1000) {
    rows.push({
      costCenter: '',
      label: 'Unallocated',
      paid: unallocatedPaid,
      allowable: null,
      excess: null,
      pctRev: null,
    });
  }

  return rows;
};

const RPYearTable: React.FC<{
  sortedYears: any[];
  reportsByYear?: Record<number, CostReport[]>;
  onDownload?: (rptRecNum: number) => void;
  downloading?: number | null;
  onMenuOpen?: (anchor: HTMLElement, reports: CostReport[]) => void;
}> = ({ sortedYears, reportsByYear, onDownload, downloading, onMenuOpen }) => {
  const [expandedYear, setExpandedYear] = useState<number | null>(null);

  const fmtDate = (iso: string) => {
    const [y, m, d] = iso.split('-');
    return `${parseInt(m)}/${parseInt(d)}/${y}`;
  };

  const thSx: React.CSSProperties = { textAlign: 'right', padding: '8px 12px', fontWeight: 700, fontSize: '0.8rem' };
  // dotted-underline hover affordance for column headers with a definition tooltip
  const thHelpSx: React.CSSProperties = { cursor: 'help', borderBottom: '1px dotted #bbb' };

  return (
    <Box sx={{ p: 2.5, backgroundColor: 'white', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
      <Typography variant="overline" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 1.5 }}>
        Year-over-Year Trend
      </Typography>
      <Box sx={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem', tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: 36 }} />
            <col style={{ width: '16%' }} />
            <col style={{ width: '16%' }} />
            <col style={{ width: '16%' }} />
            <col style={{ width: '13%' }} />
            <col style={{ width: '11%' }} />
            <col style={{ width: '13%' }} />
            {reportsByYear && <col style={{ width: '10%' }} />}
          </colgroup>
          <thead>
            <tr style={{ borderBottom: '2px solid #e0e0e0' }}>
              <th style={{ ...thSx, textAlign: 'left' }}></th>
              <th style={{ ...thSx, textAlign: 'left' }}>Year</th>
              <th style={thSx}>
                <Tooltip title="Total the facility recorded as paid to related organizations (management, real estate, staffing, etc.) that year — the amount it included in its Worksheet A costs. Source: Form CMS-2540-10, Wksht A-8-1, col. 5 ('Amount Included in Wksht. A')." arrow>
                  <span style={thHelpSx}>Paid to RP</span>
                </Tooltip>
              </th>
              <th style={thSx}>
                <Tooltip title="The related organizations' allowable cost. Under 42 CFR 413.17, related-party costs are allowable only at the cost TO the related organization — not the price it charged. Source: Form CMS-2540-10, Wksht A-8-1, col. 4 ('Amount Allowable in Cost'); 42 CFR 413.17." arrow>
                  <span style={thHelpSx}>CMS Allowable</span>
                </Tooltip>
              </th>
              <th style={thSx}>
                <Tooltip title="Worksheet A-8-1, col. 6 — the adjustment (col. 4 allowable − col. 5 paid). When the facility paid related parties more than their allowable cost, this excess is removed from reimbursable cost; shown here as its magnitude. Source: Form CMS-2540-10, Wksht A-8-1; 42 CFR 413.17." arrow>
                  <span style={thHelpSx}>Adjustment</span>
                </Tooltip>
              </th>
              <th style={thSx}>
                <Tooltip title="Related-party payments as a share of the facility's gross patient revenue that year. Higher = more of the facility's revenue flowing to insiders." arrow>
                  <span style={thHelpSx}>% of Revenue</span>
                </Tooltip>
              </th>
              <th style={thSx}>
                <Tooltip title="Related-party payments divided by resident-days, so facilities of different sizes compare on the same scale." arrow>
                  <span style={thHelpSx}>Paid / Resident-Day</span>
                </Tooltip>
              </th>
              {reportsByYear && (
                <th style={{ ...thSx, textAlign: 'center' }}>
                  <Tooltip title="Download the underlying CMS (HCRIS) cost report(s) filed for this fiscal year." arrow>
                    <span style={{ cursor: 'help' }}>Cost Report</span>
                  </Tooltip>
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {sortedYears.map((fy: any) => {
              const m = fy.metadata || fy;
              const rpPaid = m.rp_total_paid ?? m.rp_total;
              const rpAllowable = m.rp_total;
              const rpAdj = m.rp_total_adj;
              const rpPct = m.rp_total_paid_pct_gross_rev ?? m.rp_total_pct_gross_rev;
              const rpPerDay = m.rp_total_paid_per_resident_day ?? m.rp_total_per_resident_day;
              const hasExcess = rpAdj != null && rpAdj < 0;
              const isExpanded = expandedYear === fy.year;
              const costCenters = buildCostCenters(fy);

              return (
                <React.Fragment key={fy.year}>
                  <tr
                    onClick={() => setExpandedYear(isExpanded ? null : fy.year)}
                    style={{ borderBottom: isExpanded ? 'none' : '1px solid #f0f0f0', cursor: 'pointer', backgroundColor: isExpanded ? '#fafbfc' : undefined }}
                  >
                    <td style={{ padding: '8px 4px 8px 12px' }}>
                      <IconButton size="small" sx={{ p: 0, transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
                        <ExpandMoreIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </td>
                    <td style={{ padding: '8px 12px', fontWeight: 600 }}>{fy.year}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600 }}>{rpFmtMoney(rpPaid)}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', color: '#666' }}>{rpPaid ? rpFmtMoney(rpAllowable) : '-'}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: hasExcess ? 600 : undefined }}>
                      {hasExcess ? rpFmtMoney(Math.abs(rpAdj)) : '-'}
                    </td>
                    <td style={{ padding: '8px 12px', textAlign: 'right' }}>{rpPct != null ? `${rpPct.toFixed(1)}%` : '-'}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right' }}>{rpPerDay != null ? `$${rpPerDay.toFixed(2)}` : '-'}</td>
                    {reportsByYear && (() => {
                      const reportsForYear = reportsByYear[fy.year] || [];
                      if (reportsForYear.length === 0) return <td style={{ textAlign: 'center', padding: '8px 12px' }}>-</td>;
                      if (reportsForYear.length === 1) {
                        const cr = reportsForYear[0];
                        return (
                          <td style={{ textAlign: 'center', padding: '8px 12px' }}>
                            <Tooltip title={`${fmtDate(cr.fy_bgn_dt)} – ${fmtDate(cr.fy_end_dt)}`} arrow placement="left">
                              <IconButton size="small" onClick={(e) => { e.stopPropagation(); onDownload?.(cr.rpt_rec_num); }} disabled={downloading === cr.rpt_rec_num} sx={{ p: 0.5 }}>
                                <DownloadIcon sx={{ fontSize: 16, color: downloading === cr.rpt_rec_num ? 'text.disabled' : 'primary.main' }} />
                              </IconButton>
                            </Tooltip>
                          </td>
                        );
                      }
                      return (
                        <td style={{ textAlign: 'center', padding: '8px 12px' }}>
                          <Tooltip title={`${reportsForYear.length} cost reports`} arrow placement="left">
                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); onMenuOpen?.(e.currentTarget, reportsForYear); }} sx={{ p: 0.5 }}>
                              <DownloadIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                            </IconButton>
                          </Tooltip>
                        </td>
                      );
                    })()}
                  </tr>
                  {/* Expanded cost center breakdown */}
                  {isExpanded && costCenters.length > 0 && (
                    <>
                      {/* Header row for cost categories */}
                      <tr style={{ backgroundColor: '#f5f6f7', borderTop: '1px solid #e0e0e0', borderBottom: '1px solid #e8e8e8' }}>
                        <td style={{ padding: '5px 4px 5px 12px' }}></td>
                        <td style={{ padding: '5px 12px' }}>
                          <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            Cost Category
                          </Typography>
                        </td>
                        <td style={{ padding: '5px 12px', textAlign: 'right' }}>
                          <Tooltip arrow title="What this cost center paid the related party — the amount included in Worksheet A costs (Form CMS-2540-10, Wksht A-8-1, col. 5).">
                            <Typography component="span" sx={{ fontSize: '0.7rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, cursor: 'help', borderBottom: '1px dotted #bbb', display: 'inline-block' }}>Paid to RP</Typography>
                          </Tooltip>
                        </td>
                        <td style={{ padding: '5px 12px', textAlign: 'right' }}>
                          <Tooltip arrow title="The related party's allowable cost for this cost center — under 42 CFR 413.17, only the cost TO the related organization is allowable (Wksht A-8-1, col. 4).">
                            <Typography component="span" sx={{ fontSize: '0.7rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, cursor: 'help', borderBottom: '1px dotted #bbb', display: 'inline-block' }}>CMS Allowable</Typography>
                          </Tooltip>
                        </td>
                        <td style={{ padding: '5px 12px', textAlign: 'right' }}>
                          <Tooltip arrow title="Paid above the allowable cost for this cost center (Wksht A-8-1, col. 6 = col. 4 − col. 5), disallowed under 42 CFR 413.17. Shown as its magnitude.">
                            <Typography component="span" sx={{ fontSize: '0.7rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, cursor: 'help', borderBottom: '1px dotted #bbb', display: 'inline-block' }}>Adjustment</Typography>
                          </Tooltip>
                        </td>
                        <td style={{ padding: '5px 12px' }} colSpan={reportsByYear ? 3 : 2}></td>
                      </tr>
                      {costCenters.map((cc, ccIdx) => {
                        const ccHasExcess = cc.excess != null && cc.excess < 0;
                        return (
                          <tr key={`${fy.year}-${ccIdx}`} style={{ backgroundColor: '#fafbfc', borderBottom: '1px solid #f5f5f5' }}>
                            <td style={{ padding: '6px 4px 6px 12px' }}></td>
                            <td style={{ padding: '6px 12px', fontSize: '0.8rem' }}>
                              <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                                {cc.label}
                              </Typography>
                            </td>
                            <td style={{ padding: '6px 12px', textAlign: 'right', fontSize: '0.8rem' }}>{rpFmtMoney(cc.paid)}</td>
                            <td style={{ padding: '6px 12px', textAlign: 'right', fontSize: '0.8rem', color: '#999' }}>{rpFmtMoney(cc.allowable)}</td>
                            <td style={{ padding: '6px 12px', textAlign: 'right', fontSize: '0.8rem', fontWeight: ccHasExcess ? 600 : undefined }}>
                              {ccHasExcess ? rpFmtMoney(Math.abs(cc.excess!)) : '-'}
                            </td>
                            <td style={{ padding: '6px 12px' }} colSpan={reportsByYear ? 3 : 2}></td>
                          </tr>
                        );
                      })}
                    </>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </Box>
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

interface CostReport {
  rpt_rec_num: number;
  fy_bgn_dt: string;
  fy_end_dt: string;
}

interface RelatedPartySectionProps {
  facility: FacilityData;
  facilityYears?: any[];
  rpBenchmarks?: RPBenchmarks | null;
  rpEntities?: RPEntity[];
  // Cost report download
  onGenerateCostReports?: () => void;
  costReportGenerating?: boolean;
  costReportProgress?: number;
  costReportStatus?: string;
  costReports?: CostReport[];
  schemeId?: string;
}

export const RelatedPartySection: React.FC<RelatedPartySectionProps> = ({
  facility,
  facilityYears,
  rpBenchmarks,
  rpEntities,
  onGenerateCostReports,
  costReportGenerating,
  costReportProgress,
  costReportStatus,
  costReports,
  schemeId,
}) => {
  const [crMenuAnchor, setCrMenuAnchor] = useState<null | HTMLElement>(null);
  const [crMenuReports, setCrMenuReports] = useState<CostReport[]>([]);
  const [crDownloading, setCrDownloading] = useState<number | null>(null);

  const fmtDate = (iso: string) => {
    const [y, m, d] = iso.split('-');
    return `${parseInt(m)}/${parseInt(d)}/${y}`;
  };

  // Group cost reports by fiscal year end
  const reportsByYear: Record<number, CostReport[]> = {};
  if (costReports) {
    for (const cr of costReports) {
      if (cr.fy_end_dt) {
        const year = new Date(cr.fy_end_dt).getFullYear();
        if (!reportsByYear[year]) reportsByYear[year] = [];
        reportsByYear[year].push(cr);
      }
    }
  }

  const handleCrDownload = async (rptRecNum: number) => {
    if (!schemeId) return;
    setCrDownloading(rptRecNum);
    setCrMenuAnchor(null);
    try {
      const res = await api.get(
        `/schemes/${schemeId}/download-cost-report/?rpt_rec_num=${rptRecNum}`,
        { responseType: 'blob' }
      );
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const cd = res.headers['content-disposition'];
      const match = cd?.match(/filename="?([^";\n]+)"?/);
      link.download = match ? match[1] : `cost_report_${rptRecNum}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download cost report:', err);
    } finally {
      setCrDownloading(null);
    }
  };

  // Check if facility has any related party payments
  const hasRPPayments = (facility.total_rp_total ?? 0) > 0 || (facility.total_rp_total_paid ?? 0) > 0;

  // If no RP payments, show simple message with cost report button
  if (!hasRPPayments) {
    return (
      <Box sx={{ mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" fontWeight={700}>
            Related Party Payments
          </Typography>
          {onGenerateCostReports && (
            <Box sx={{ textAlign: 'right' }}>
              <Tooltip title="Download HCRIS cost report Excel files for all fiscal years">
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={costReportGenerating ? <CircularProgress size={14} /> : <DownloadIcon />}
                  onClick={onGenerateCostReports}
                  disabled={costReportGenerating}
                  sx={{ fontSize: '0.75rem', py: 0.5 }}
                >
                  {costReportGenerating ? 'Generating...' : 'Cost Reports'}
                </Button>
              </Tooltip>
              {costReportGenerating && (
                <Box sx={{ mt: 0.5, minWidth: 140 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                    {costReportStatus}
                  </Typography>
                  <LinearProgress variant="determinate" value={costReportProgress || 0} sx={{ mt: 0.25, height: 3 }} />
                </Box>
              )}
            </Box>
          )}
        </Box>
        <Box
          sx={{
            p: 2.5,
            backgroundColor: 'grey.50',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            This facility has no related party payments reported in their cost reports.
          </Typography>
        </Box>
        <Divider sx={{ mt: 3 }} />
      </Box>
    );
  }

  const hasSignificantRP = (facility.rp_total_pct_gross_rev ?? 0) > 10;

  // Get state and national benchmarks
  const stateBenchmark = rpBenchmarks?.by_state?.[facility.state];
  const nationalBenchmark = rpBenchmarks?.national;

  // Calculate RP per resident day from facility data (prefer paid over allowable)
  const rpPerResident = facility.total_rp_total && facility.avg_census
    ? facility.total_rp_total / facility.avg_census
    : null;
  const rpPerResidentDay = facility.rp_total_paid_per_resident_day
    ?? facility.rp_total_per_resident_day
    ?? (rpPerResident ? rpPerResident / 365 : null);

  // Build comparison data for bar chart (higher = worse for RP)
  const comparisons = rpPerResidentDay != null ? [
    {
      label: 'This Facility',
      value: rpPerResidentDay,
      color: rpPerResidentDay > (stateBenchmark?.median_rp_per_resident_day ?? nationalBenchmark?.median_rp_per_resident_day ?? 0) * 1.5
        ? 'error.main'
        : rpPerResidentDay > (stateBenchmark?.median_rp_per_resident_day ?? nationalBenchmark?.median_rp_per_resident_day ?? 0)
          ? 'warning.main'
          : 'success.main',
      isHighlighted: true,
    },
    ...(stateBenchmark?.median_rp_per_resident_day != null ? [{
      label: `${facility.state} Median`,
      value: stateBenchmark.median_rp_per_resident_day,
      color: 'info.main',
      isHighlighted: false,
    }] : []),
    ...(nationalBenchmark?.median_rp_per_resident_day != null ? [{
      label: 'National Median',
      value: nationalBenchmark.median_rp_per_resident_day,
      color: 'info.main',
      isHighlighted: false,
    }] : []),
  ].sort((a, b) => b.value - a.value) : [];

  const maxBarValue = comparisons.length > 0 ? Math.max(...comparisons.map(c => c.value)) * 1.1 : 100;

  // Sort facility years by year descending for the trend table
  const sortedYears = facilityYears
    ?.filter(fy => (fy.rp_total != null && fy.rp_total > 0) || (fy.rp_total_paid != null && fy.rp_total_paid > 0))
    ?.sort((a, b) => b.year - a.year)
    ?.slice(0, 5) || [];

  return (
    <Box sx={{ mb: 4 }}>
      {/* Section Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h6" fontWeight={700}>
            Related Party Payments
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Total 2020-2024 from CMS cost reports
          </Typography>
        </Box>
        {/* Cost Reports button hidden — may reuse elsewhere */}
      </Box>

      {/* Summary Cards Row */}
      <Box display="flex" gap={2} mb={3}>
        {/* Card 1: Total Related Party Payments */}
        <Box sx={{ flex: 1, p: 2.5, backgroundColor: 'white', borderRadius: 2, border: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
          <Typography variant="overline" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 1 }}>
            Total Related Party Payments
          </Typography>
          <Typography variant="h4" fontWeight={700}>
            {formatMoney(facility.total_rp_total_paid ?? facility.total_rp_total)}
          </Typography>
          <Typography variant="caption" color="text.secondary">2020–2024</Typography>
        </Box>

        {/* Card 2: Excess Over Allowable */}
        <Box sx={{ flex: 1, p: 2.5, backgroundColor: 'white', borderRadius: 2, border: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
          <Typography variant="overline" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 1 }}>
            Payments in Excess of Allowable
          </Typography>
          <Typography variant="h4" fontWeight={700}>
            {(facility.total_rp_total_adj ?? 0) < 0 ? formatMoney(Math.abs(facility.total_rp_total_adj!)) : '$0'}
          </Typography>
          <Typography variant="caption" color="text.secondary">2020–2024</Typography>
        </Box>

        {/* Card 3: RP Per Resident Day with comparison */}
        <Box sx={{ flex: 1.5, p: 2.5, backgroundColor: 'white', borderRadius: 2, border: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
          <Typography variant="overline" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 1 }}>
            RP Payments Per Resident Day
          </Typography>
          {(() => {
            const facilityVal = rpPerResidentDay;
            const stateVal = stateBenchmark?.median_rp_per_resident_day;
            const nationalVal = nationalBenchmark?.median_rp_per_resident_day;
            const isAboveSt = facilityVal != null && stateVal != null && facilityVal > stateVal;
            const facilityColor = isAboveSt ? '#d32f2f' : '#2e7d32';
            const chartItems = [
              ...(facilityVal != null ? [{ label: 'This Facility', value: facilityVal, color: facilityColor, bold: true }] : []),
              ...(stateVal != null ? [{ label: `${facility.state} Median`, value: stateVal, color: '#90caf9', bold: false }] : []),
              ...(nationalVal != null ? [{ label: 'National Median', value: nationalVal, color: '#90caf9', bold: false }] : []),
            ].sort((a, b) => b.value - a.value);
            const maxVal = Math.max(...chartItems.map(i => i.value), 1) * 1.1;
            return (
              <>
                <Typography variant="h4" fontWeight={700}>
                  {facilityVal != null ? `$${facilityVal.toFixed(2)}` : '-'}
                  <Typography component="span" variant="caption" color="text.secondary">/day</Typography>
                </Typography>
                {chartItems.length > 1 && (
                  <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {chartItems.map((item, idx) => {
                      const barW = (item.value / maxVal) * 100;
                      return (
                        <Box key={idx} sx={{ textAlign: 'left' }}>
                          <Box display="flex" justifyContent="space-between" mb={0.15}>
                            <Typography sx={{ fontSize: '0.65rem', fontWeight: item.bold ? 700 : 500, color: item.bold ? item.color : 'text.secondary' }}>{item.label}</Typography>
                            <Typography sx={{ fontSize: '0.65rem', fontWeight: item.bold ? 700 : 600, color: item.bold ? item.color : 'text.secondary' }}>${item.value.toFixed(2)}/day</Typography>
                          </Box>
                          <Box sx={{ height: item.bold ? 10 : 6, backgroundColor: 'grey.200', borderRadius: 1, overflow: 'hidden' }}>
                            <Box sx={{ height: '100%', width: `${barW}%`, backgroundColor: item.color, borderRadius: 1 }} />
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </>
            );
          })()}
        </Box>

        {/* Card 4: RP as % of Revenue with comparison */}
        <Box sx={{ flex: 1.5, p: 2.5, backgroundColor: 'white', borderRadius: 2, border: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
          <Typography variant="overline" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 1 }}>
            RP Payments as % of Revenue
          </Typography>
          {(() => {
            const facilityVal = facility.rp_total_paid_pct_gross_rev ?? facility.rp_total_pct_gross_rev;
            const stateVal = stateBenchmark?.median_rp_pct;
            const nationalVal = nationalBenchmark?.median_rp_pct;
            const isAboveSt = facilityVal != null && stateVal != null && facilityVal > stateVal;
            const facilityColor = isAboveSt ? '#d32f2f' : '#2e7d32';
            const chartItems = [
              ...(facilityVal != null ? [{ label: 'This Facility', value: facilityVal, color: facilityColor, bold: true }] : []),
              ...(stateVal != null ? [{ label: `${facility.state} Median`, value: stateVal, color: '#90caf9', bold: false }] : []),
              ...(nationalVal != null ? [{ label: 'National Median', value: nationalVal, color: '#90caf9', bold: false }] : []),
            ].sort((a, b) => b.value - a.value);
            const maxVal = Math.max(...chartItems.map(i => i.value), 1) * 1.1;
            return (
              <>
                <Typography variant="h4" fontWeight={700}>
                  {facilityVal != null ? `${facilityVal.toFixed(1)}%` : '-'}
                </Typography>
                {chartItems.length > 1 && (
                  <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {chartItems.map((item, idx) => {
                      const barW = (item.value / maxVal) * 100;
                      return (
                        <Box key={idx} sx={{ textAlign: 'left' }}>
                          <Box display="flex" justifyContent="space-between" mb={0.15}>
                            <Typography sx={{ fontSize: '0.65rem', fontWeight: item.bold ? 700 : 500, color: item.bold ? item.color : 'text.secondary' }}>{item.label}</Typography>
                            <Typography sx={{ fontSize: '0.65rem', fontWeight: item.bold ? 700 : 600, color: item.bold ? item.color : 'text.secondary' }}>{item.value.toFixed(1)}%</Typography>
                          </Box>
                          <Box sx={{ height: item.bold ? 10 : 6, backgroundColor: 'grey.200', borderRadius: 1, overflow: 'hidden' }}>
                            <Box sx={{ height: '100%', width: `${barW}%`, backgroundColor: item.color, borderRadius: 1 }} />
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </>
            );
          })()}
        </Box>
      </Box>


      {/* Year-over-Year Trend Table with expandable cost centers */}
      {sortedYears.length > 0 && (
        <RPYearTable
          sortedYears={sortedYears}
          reportsByYear={reportsByYear}
          onDownload={handleCrDownload}
          downloading={crDownloading}
          onMenuOpen={(anchor, reports) => { setCrMenuAnchor(anchor); setCrMenuReports(reports); }}
        />
      )}

      {/* Menu for selecting among multiple cost reports in a single year */}
      <Menu anchorEl={crMenuAnchor} open={Boolean(crMenuAnchor)} onClose={() => setCrMenuAnchor(null)}>
        {crMenuReports.map((cr) => (
          <MenuItem
            key={cr.rpt_rec_num}
            onClick={() => handleCrDownload(cr.rpt_rec_num)}
            disabled={crDownloading === cr.rpt_rec_num}
            sx={{ fontSize: '0.8rem' }}
          >
            <DownloadIcon sx={{ fontSize: 16, mr: 1, color: 'primary.main' }} />
            {fmtDate(cr.fy_bgn_dt)} – {fmtDate(cr.fy_end_dt)}
          </MenuItem>
        ))}
      </Menu>

      {/* Related Party Entities */}
      {rpEntities && rpEntities.length > 0 && (
        <RPEntitiesSection entities={rpEntities} />
      )}

      <Divider sx={{ mt: 3 }} />
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

const FacilityDetailView: React.FC<FacilityDetailViewProps> = ({
  facility,
  schemeId,
  facilityYears,
  sweetwaterBaseline,
  citationBenchmarks,
  rpBenchmarks,
  rpEntities,
  turnoverBenchmarks,
  onGenerateCostReports,
  costReportGenerating,
  costReportProgress,
  costReportStatus,
}) => {
  const [acuityModel, setAcuityModel] = useState<AcuityModel>('anticipated');

  // Default baselines (these would ideally come from API)
  const baselines = {
    state: {
      hours_short_per_resident_day: 0.15,
      pct_days_below_minimum: 20,
    },
    sweetwater: {
      hours_short_per_resident_day: sweetwaterBaseline?.understaffed_state_min_hprd_hrs_per_res_day ?? 0.08,
      pct_days_below_minimum: sweetwaterBaseline?.pct_days_below_cms_hprd ?? 12,
    },
  };

  return (
    <Box>
      {/* 1. Understaffing Summary (includes HPRD comparison) */}
      <UnderstaffingSummary
        facilityYears={facilityYears || []}
        facilityYearsHybrid={[]}
        facility={facility}
        state={facility.state}
        stateContext={undefined}
        model={acuityModel}
        onModelChange={setAcuityModel}
      />

      {/* 2. Staffing Trend Over Time */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" fontWeight={700} mb={2}>
          Staffing Over Time
        </Typography>
        <StaffingTrendCard ccn={facility.ccn} />
      </Box>

      {/* 3. Prior Conduct */}
      <PriorConduct
        ccn={facility.ccn}
        schemeId={schemeId}
        facilityYears={facilityYears}
        state={facility.state}
        beds={facility.beds}
        facilityMetrics={{
          total_harm_citations: facility.total_harm_citations,
          total_jeopardy_citations: facility.total_jeopardy_citations,
          harm_per_100_residents: facility.harm_per_100_residents,
          total_citations_per_100: facility.total_citations_per_100,
          total_citations: facility.total_citations,
          total_fine_amount: facility.total_fine_amount,
          staffing_ftags_total: facility.staffing_ftags_total,
          staffing_harm_total: facility.staffing_harm_total,
          // F-tag category totals
          total_staffing_ftags: facility.total_staffing_ftags,
          total_abuse_neglect_ftags: facility.total_abuse_neglect_ftags,
          total_quality_of_care_ftags: facility.total_quality_of_care_ftags,
          total_infection_control_ftags: facility.total_infection_control_ftags,
          total_pharmacy_ftags: facility.total_pharmacy_ftags,
          total_behavioral_health_ftags: facility.total_behavioral_health_ftags,
          total_dietary_ftags: facility.total_dietary_ftags,
        }}
        citationBenchmarks={citationBenchmarks}
      />

      {/* 4. Related Party Payments */}
      <RelatedPartySection
        facility={facility}
        facilityYears={facilityYears}
        rpBenchmarks={rpBenchmarks}
        rpEntities={rpEntities}
        onGenerateCostReports={onGenerateCostReports}
        costReportGenerating={costReportGenerating}
        costReportProgress={costReportProgress}
        costReportStatus={costReportStatus}
      />

      {/* 5. Wage Analysis - hidden for now */}
      {/* <WageAnalysisSection facility={facility} /> */}
    </Box>
  );
};

export default FacilityDetailView;
